import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { useLeadStages, useLeads, useUpdateLead } from '@/hooks/useLeads'
import { useToast } from '@/hooks/use-toast'
import { LeadKanbanColumn } from './LeadKanbanColumn'
import { LeadCard } from './LeadCard'
import { Skeleton } from '@/components/ui/skeleton'
import type { LeadWithRelations, LeadFilters } from '@limo/shared'

interface LeadKanbanBoardProps {
  filters?: LeadFilters
}

export function LeadKanbanBoard({ filters = {} }: LeadKanbanBoardProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Fetch data
  const { data: stages, isLoading: stagesLoading } = useLeadStages()
  const { data: leadsData, isLoading: leadsLoading } = useLeads(
    { ...filters, status: filters.status || ['open'] }, // Default to open leads only
    { field: 'created_at', direction: 'desc' },
    1,
    500 // Get more leads for kanban view
  )

  // Mutations
  const updateLead = useUpdateLead()

  // DnD state
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  // Filter out Won/Lost stages - they belong in the Archive, not the Kanban
  const activeStages = useMemo(() => {
    return stages?.filter(s => !s.isWon && !s.isLost) || []
  }, [stages])

  // Collapsed stages state (none collapsed by default since won/lost are removed)
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set())

  // Sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    })
  )

  // Group leads by stage (only active stages)
  const leadsByStage = useMemo(() => {
    const grouped: Record<string, LeadWithRelations[]> = {}
    activeStages.forEach(stage => {
      grouped[stage.id] = []
    })
    leadsData?.leads.forEach(lead => {
      if (grouped[lead.stageId]) {
        grouped[lead.stageId].push(lead)
      }
    })
    return grouped
  }, [activeStages, leadsData?.leads])

  // Find active lead for drag overlay
  const activeLead = useMemo(() => {
    if (!activeId) return null
    return leadsData?.leads.find(l => l.id === activeId) || null
  }, [activeId, leadsData?.leads])

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  // Handle drag over (for visual feedback)
  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string || null)
  }

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setOverId(null)

    if (!over || !activeStages.length) return

    const leadId = active.id as string
    const lead = leadsData?.leads.find(l => l.id === leadId)
    if (!lead) return

    // Determine target stage (could be a stage or another lead in a stage)
    let targetStageId: string
    const isStage = activeStages.some(s => s.id === over.id)

    if (isStage) {
      targetStageId = over.id as string
    } else {
      // Dropped on another lead - find its stage
      const targetLead = leadsData?.leads.find(l => l.id === over.id)
      if (!targetLead) return
      targetStageId = targetLead.stageId
    }

    // No change if same stage
    if (lead.stageId === targetStageId) return

    const targetStage = activeStages.find(s => s.id === targetStageId)
    if (!targetStage) return

    // Move to stage (won/lost stages are not in activeStages, so no need to check)
    try {
      await updateLead.mutateAsync({ leadId, stageId: targetStageId })
      toast({
        title: 'Lead moved',
        description: `Moved to "${targetStage.name}"`,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to move lead',
        variant: 'destructive',
      })
    }
  }

  // Toggle collapsed state
  const toggleCollapse = (stageId: string) => {
    setCollapsedStages(prev => {
      const next = new Set(prev)
      if (next.has(stageId)) {
        next.delete(stageId)
      } else {
        next.add(stageId)
      }
      return next
    })
  }

  // Handle lead click
  const handleLeadClick = (leadId: string) => {
    navigate(`${basePath}/leads/${leadId}`)
  }

  // Loading state
  if (stagesLoading || leadsLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex-shrink-0 w-72">
            <Skeleton className="h-10 w-full rounded-t-lg" />
            <Skeleton className="h-[400px] w-full rounded-b-lg" />
          </div>
        ))}
      </div>
    )
  }

  if (!activeStages || activeStages.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No pipeline stages configured. Please set up your lead stages first.
      </div>
    )
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
          {activeStages.map((stage) => (
            <LeadKanbanColumn
              key={stage.id}
              stage={stage}
              leads={leadsByStage[stage.id] || []}
              onLeadClick={handleLeadClick}
              isCollapsed={collapsedStages.has(stage.id)}
              onToggleCollapse={() => toggleCollapse(stage.id)}
            />
          ))}
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeLead && (
            <div className="w-72">
              <LeadCard lead={activeLead} isDragging />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </>
  )
}
